import React, { useState } from 'react';
import { Cell, flexRender, Row, VisibilityState } from '@tanstack/react-table';
import { ColumnId } from '@/experiences/sales-portal/domain/pricing/screens/management/types';
import { createStyles } from '@/contexts/theme-context';
import {
  isDeltaColumn,
  isInputColumn,
  isLeftAlignedColumn,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { Pressable } from 'react-native';
import { PRICING_TABLE_LAYOUT } from '@/experiences/sales-portal/domain/pricing/constants';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';

interface PricingTableRowProps<T> {
  /**
   * Drives memo invalidation when the user reorders columns. Read by
   * `row.getVisibleCells()` via TanStack table state, but listed here so
   * `React.memo`'s shallow compare picks up reorder changes.
   */
  columnOrder?: string[];
  /**
   * Drives memo invalidation when the user toggles column visibility. Same
   * rationale as `columnOrder` — kept in props purely to bust the memo.
   */
  columnVisibility?: VisibilityState;
  hasChanges: boolean;
  /**
   * Renders a subtle grey background at rest (used by the Bookmarked page to
   * distinguish account rows). `hasChanges` (blue) and hover still take
   * priority. Defaults to false, so other tables are unaffected.
   */
  hasGreyBackground?: boolean;
  index: number;
  isSelected: boolean;
  row: Row<T>;
}

const PricingItemDetailsTableRowImpl = <T,>({
  hasChanges,
  hasGreyBackground = false,
  index,
  isSelected,
  row,
}: PricingTableRowProps<T>): React.ReactElement => {
  const [isHovered, setIsHovered] = useState(false);
  const [styles] = useStyles();

  const handleHoverIn = () => setIsHovered(true);
  const handleHoverOut = () => setIsHovered(false);

  const changedStyle =
    hasChanges && isHovered
      ? [styles.cellWithChanges, styles.cellWithChangesHovered]
      : hasChanges
        ? styles.cellWithChanges
        : {};

  const hoveredStyle = hasChanges
    ? changedStyle
    : isHovered || isSelected
      ? styles.hovered
      : hasGreyBackground
        ? styles.restingGrey
        : {};

  const cellStyle = [styles.cell, hoveredStyle, changedStyle];
  const deltaCellStyle = [styles.deltaCell, hoveredStyle, changedStyle];

  return (
    <Pressable
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      tabIndex={-1}
      testID={`pricing-table-row-${row.index}`}
    >
      <TableRow
        aria-selected={isSelected}
        index={index}
        key={row.id}
        style={styles.tableRow}
      >
        {row.getVisibleCells().map((cell: Cell<T, unknown>) => {
          const inputCellStyle = isInputColumn(cell.column.id as ColumnId)
            ? styles.inputCell
            : {};

          return (
            <TableCell
              cell={cell}
              key={cell.id}
              style={
                isLeftAlignedColumn(cell.column.id as ColumnId)
                  ? hoveredStyle
                  : isDeltaColumn(cell.column.id as ColumnId)
                    ? [deltaCellStyle, inputCellStyle]
                    : [changedStyle, cellStyle, inputCellStyle]
              }
            >
              {flexRender(cell.column.columnDef.cell, {
                ...cell.getContext(),
                index,
              })}
            </TableCell>
          );
        })}
      </TableRow>
    </Pressable>
  );
};

export const PricingItemDetailsTableRow = React.memo(
  PricingItemDetailsTableRowImpl,
) as typeof PricingItemDetailsTableRowImpl;

const useStyles = createStyles((theme) => ({
  cell: {
    alignItems: 'flex-end',
    paddingRight:
      theme.sk.spacing.spaceSmall + PRICING_TABLE_LAYOUT.SORT_ARROW_WIDTH,
  },
  cellWithChanges: {
    backgroundColor: theme.sk.color.bg.highlight.blue.default,
    justifyContent: 'flex-start',
    paddingTop: 14,
  },
  cellWithChangesHovered: {
    backgroundColor: theme.sk.color.bg.highlight.blue.hover,
  },
  deltaCell: {
    alignItems: 'flex-end',
    paddingRight: theme.sk.spacing.spaceSmall,
  },
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  inputCell: {
    paddingRight: theme.sk.spacing.spaceSmall - 1, // numeric base minus sorting arrow and 1px input border
    paddingTop: 8,
  },
  restingGrey: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  tableRow: {
    overflow: 'visible',
  },
}));
